import { createSlice } from "infrastructure/store/lib";
import { sliceMixin } from "infrastructure/store/reducers";
import { ApiState } from "infrastructure/api";
import { compareIds, when } from "util/support";
import {
    deleteUserRequestFulfilled,
    rolesRequestFulfilled,
    rolesRequestLoading,
    rolesRequestRejected,
    userByIdRequestFilfilled,
    usersMerchantsRequestFulfilled,
    usersMerchantsRequestLoading,
    usersMerchantsRequestRejected,
    usersMorsRequestFulfilled,
    usersMorsRequestLoading,
    usersMorsRequestRejected,
    usersRequestFulfilled,
    usersRequestLoading,
    usersRequestRejected
} from "./reducers";
import initialState, { sliceToken } from "./state";
import {
    deleteUserThunk,
    getRolesThunk,
    getUserByIdThunk,
    getUsersMerchantsThunk,
    getUsersMorsThunk,
    getUsersThunk,
    postUserThunk
} from "./thunks";
import type { UsersState } from "./types";

const slice = createSlice({
    name: sliceToken,
    initialState,
    reducers: {
        ...sliceMixin<UsersState>(initialState),
        updateUser: (state, { payload }) => {
            state.usersSlice!.data = state.usersSlice!.data.map(user => when(
                compareIds(user.coreId, payload.coreId),
                {
                    ...user,
                    ...payload
                },
                user
            ));
        },
        changeUsersLoadingState: (state, { payload }) => {
            state.usersLoadingState = payload;
        },
        changeUsersSlice: (state, { payload }) => {
            state.usersSlice = payload.data;
        },
        changeRolesLoadingState: (state, { payload }) => {
            state.rolesLoadingState = payload;
        },
        changeRolesSlice: (state, { payload }) => {
            state.rolesSlice = payload.data;
        },
    },
    extraReducers: builder => {
        builder
            // Users
            .addCase(getUsersThunk.pending, usersRequestLoading)
            .addCase(getUsersThunk.fulfilled, usersRequestFulfilled)
            .addCase(getUsersThunk.rejected, usersRequestRejected)
            // User by ID
            .addCase(getUserByIdThunk.pending, usersRequestLoading)
            .addCase(getUserByIdThunk.fulfilled, userByIdRequestFilfilled)
            .addCase(getUserByIdThunk.rejected, usersRequestRejected)
            // Create or update User
            .addCase(postUserThunk.pending, state => {
                state.userProfileLoadingState = ApiState.Pending;
            })
            .addCase(postUserThunk.fulfilled, state => {
                state.userProfileLoadingState = ApiState.Succeeded;
            })
            .addCase(postUserThunk.rejected, state => {
                state.userProfileLoadingState = ApiState.Failed;
            })
            // Delete User
            .addCase(deleteUserThunk.pending, usersRequestLoading)
            .addCase(deleteUserThunk.fulfilled, deleteUserRequestFulfilled)
            .addCase(deleteUserThunk.rejected, usersRequestRejected)
            // Roles
            .addCase(getRolesThunk.pending, rolesRequestLoading)
            .addCase(getRolesThunk.fulfilled, rolesRequestFulfilled)
            .addCase(getRolesThunk.rejected, rolesRequestRejected)
            // UsersMerchantsPivot
            .addCase(getUsersMerchantsThunk.pending, usersMerchantsRequestLoading)
            .addCase(getUsersMerchantsThunk.fulfilled, usersMerchantsRequestFulfilled)
            .addCase(getUsersMerchantsThunk.rejected, usersMerchantsRequestRejected)
            // UsersMorsPivot
            .addCase(getUsersMorsThunk.pending, usersMorsRequestLoading)
            .addCase(getUsersMorsThunk.fulfilled, usersMorsRequestFulfilled)
            .addCase(getUsersMorsThunk.rejected, usersMorsRequestRejected)
    }
});

export const {
    name,
    actions: {
        updateUser,
        changeUsersLoadingState,
        changeUsersSlice,
        changeRolesLoadingState,
        changeRolesSlice,
        changeExportLoading,
        resetState
    }
} = slice;

export default slice.reducer;
